import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePatientStore } from '../stores/patientSearchStore';

import { ROUTES } from '@/routes/constants';

/**
 * Hook para manejar el boundary de la feature medical-record
 * - Log de navegación
 * - Reset del store cuando se sale de la feature
 */
export const useFeatureBoundary = () => {
  const location = useLocation();
  const { resetAll, formValues, searchParams, hasSearched, selectedPatient } = usePatientStore();
  const prevPathnameRef = useRef<string>('');
  const isMountedRef = useRef(false);
  
  // Determinar si estamos dentro de la feature
  const isWithinFeature = location.pathname.includes(ROUTES.MEDICAL_RECORD);
  
  useEffect(() => {
    const currentPathname = location.pathname;
    const prevPathname = prevPathnameRef.current;
    
    // Inicialización en el primer mount
    if (!isMountedRef.current) {
      isMountedRef.current = true;
    }

    // Reset del store si salimos de la feature
    if (prevPathname && !isWithinFeature && prevPathname.includes(ROUTES.MEDICAL_RECORD)) {
      resetAll();
    }
    
    // Actualizar referencia
    prevPathnameRef.current = currentPathname;
  }, [location.pathname, isWithinFeature, resetAll, formValues, hasSearched, selectedPatient, searchParams]);
  
  // Log cuando el componente se desmonta y ejecutar reset si es necesario
  useEffect(() => {
    return () => {
      if (isMountedRef.current) {
        const currentPath = location.pathname;
        const isLeavingFeature = !currentPath.includes(ROUTES.MEDICAL_RECORD);
        // Reset del store si estamos saliendo de la feature
        if (isLeavingFeature) {
          resetAll();
        }
      }
    };
  }, [location.pathname, formValues, hasSearched, selectedPatient, searchParams, resetAll]);
  
  return {
    isWithinFeature,
  };
};
